import { useEffect, useState, useRef } from 'react'
import axios from 'axios'
import { FiBell, FiTrash2, FiX, FiMessageCircle } from 'react-icons/fi'

export default function CustomerNotificationCenter({ website, customerAuthToken, customerAuthUser, onOpenConversation }) {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [showPanel, setShowPanel] = useState(false)
  const [loading, setLoading] = useState(false)
  const panelRef = useRef(null)

  const fetchNotifications = async () => {
    if (!customerAuthToken) return

    try {
      setLoading(true)
      const response = await axios.get('/customer-notifications/me', {
        params: { page: 1, limit: 10 },
        headers: { Authorization: `Bearer ${customerAuthToken}` }
      })
      setNotifications(response.data.data || [])
      setUnreadCount(response.data.unreadCount || 0)
    } catch (error) {
      console.error('[CustomerNotificationCenter] Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchUnreadCount = async () => {
    if (!customerAuthToken) return

    try {
      const response = await axios.get('/customer-notifications/me/unread-count', {
        headers: { Authorization: `Bearer ${customerAuthToken}` }
      })
      setUnreadCount(response.data.unreadCount || 0)
    } catch (error) {
      console.error('[CustomerNotificationCenter] Error fetching unread count:', error)
    }
  }

  useEffect(() => {
    fetchNotifications()
    // Fetch unread count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000)
    return () => clearInterval(interval)
  }, [customerAuthToken])

  // Close panel when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setShowPanel(false)
      }
    }

    if (showPanel) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showPanel])

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axios.post(`/customer-notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${customerAuthToken}` }
      })
      setNotifications((prev) =>
        prev.map((n) => n._id === notificationId ? { ...n, read: true } : n)
      )
      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch (error) {
      console.error('[CustomerNotificationCenter] Error marking notification as read:', error)
    }
  }

  const handleDeleteNotification = async (notificationId) => {
    try {
      await axios.delete(`/customer-notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${customerAuthToken}` }
      })
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId))
      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch (error) {
      console.error('[CustomerNotificationCenter] Error deleting notification:', error)
    }
  }

  const handleClearAll = async () => {
    if (!window.confirm('Delete all notifications?')) return

    try {
      await axios.post('/customer-notifications/me/clear-all', {}, {
        headers: { Authorization: `Bearer ${customerAuthToken}` }
      })
      setNotifications([])
      setUnreadCount(0)
      setShowPanel(false)
    } catch (error) {
      console.error('[CustomerNotificationCenter] Error clearing notifications:', error)
    }
  }

  const handleOpenConversation = (notification) => {
    handleMarkAsRead(notification._id)
    if (onOpenConversation) {
      onOpenConversation(notification.conversationId)
      setShowPanel(false)
    }
  }

  if (!customerAuthUser || !customerAuthToken) {
    return null
  }

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="relative inline-flex items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition"
        title="Notifications"
      >
        <FiBell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {showPanel && (
        <div className="absolute right-0 mt-2 w-96 max-h-96 bg-white rounded-lg border border-gray-200 shadow-lg z-50 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">
              Notifications {unreadCount > 0 && <span className="text-red-500">({unreadCount})</span>}
            </h3>
            <button
              onClick={() => setShowPanel(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>

          {/* Notification List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">No notifications</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification._id}
                    className={`px-4 py-3 hover:bg-gray-50 transition ${!notification.read ? 'bg-blue-50' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <FiMessageCircle className={`w-4 h-4 mt-1 flex-shrink-0 ${!notification.read ? 'text-blue-600' : 'text-gray-400'}`} />
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => handleOpenConversation(notification)}
                          className="text-left hover:text-blue-600 transition"
                        >
                          <p className={`text-sm font-semibold text-gray-900 ${!notification.read ? 'font-bold' : ''}`}>
                            {notification.title}
                          </p>
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{notification.message}</p>
                          {notification.conversationSubject && (
                            <p className="text-xs text-gray-500 mt-1 font-medium">
                              Re: {notification.conversationSubject}
                            </p>
                          )}
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                          {new Date(notification.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteNotification(notification._id)}
                        className="text-gray-400 hover:text-red-600 flex-shrink-0"
                        title="Delete notification"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200">
              <button
                onClick={handleClearAll}
                className="w-full py-2 px-3 text-sm text-red-600 hover:bg-red-50 rounded transition font-medium"
              >
                Clear All
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
